// Directus API utilities

import {
  createDirectus,
  rest,
  staticToken,
  readItems,
  readItem,
  createItem,
  updateItem,
  deleteItem,
} from '@directus/sdk';

import { DirectusSchema } from '../../lib/directusSchema';

// 型エラー回避のため、Directus SDKの関数を any型でラップ
const readItemsAny = readItems as any;
const readItemAny = readItem as any;
const createItemAny = createItem as any;
const updateItemAny = updateItem as any;
const deleteItemAny = deleteItem as any;

// Directusクライアントの型定義
type DirectusClient = any;

// 型定義はDirectusSchemaからインポート

// 環境変数
const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL || 'http://localhost:8055';
const DIRECTUS_TOKEN = process.env.REACT_APP_DIRECTUS_TOKEN;

// Directusクライアントの作成
let directusClient: DirectusClient;

if (DIRECTUS_TOKEN) {
  // 本番環境: staticTokenを使用
  directusClient = createDirectus<DirectusSchema>(DIRECTUS_URL)
    .with(rest())
    .with(staticToken(DIRECTUS_TOKEN)) as any;
} else {
  // 開発環境: authenticationを使用（後でトークンを設定）
  directusClient = createDirectus<DirectusSchema>(DIRECTUS_URL).with(rest()) as any;

  console.warn(
    'Directus認証トークンが設定されていません。環境変数REACT_APP_DIRECTUS_TOKENを確認してください。'
  );
}

export { directusClient };

// エイリアス関数（後方互換性のため）
export const getDirectusClient = () => directusClient;

// エラーハンドリング関数
export const handleDirectusError = (error: any, context: string) => {
  console.error(`Directus error in ${context}:`, error);

  // 認証エラーの詳細ログ
  if (error?.status === 401) {
    console.error('🔐 Directus認証エラーが発生しました:');
    console.error('📝 以下の手順で新しいトークンを取得してください:');
    console.error('   1. https://api.interestic.com/admin にアクセス');
    console.error('   2. 管理者アカウントでログイン');
    console.error('   3. 設定 > アクセストークン > 新しいトークンを作成');
    console.error('   4. .envファイルのREACT_APP_DIRECTUS_TOKENを更新');
    console.error('   5. 開発サーバーを再起動');
    console.error('💡 現在のトークンが無効または期限切れの可能性があります');
  }

  if (error?.message) {
    return {
      message: error.message,
      status: error.status || 500,
      context,
    };
  }

  return {
    message: 'Unknown error occurred',
    status: 500,
    context,
  };
};

// 定数
export const CREATURES_COLLECTION = 'creatures';
export const AGES_COLLECTION = 'ages';
export const ERAS_COLLECTION = 'eras';
export const PERIODS_COLLECTION = 'periods';
export const EONS_COLLECTION = 'eons';
export const EPOCHS_COLLECTION = 'epochs';
export const USERS_COLLECTION = 'directus_users';
export const USER_PROFILES_COLLECTION = 'user_profiles';
export const FAVORITES_COLLECTION = 'favorites';
export const AI_ARTICLES_COLLECTION = 'ai_articles';
export const DUPLICATION_ARTICLES_COLLECTION = 'duplication_articles';
export const COMMENTS_COLLECTION = 'comments';

// 恐竜データの取得
export const getCreatures = async (params?: {
  filters?: Record<string, any>;
  sort?: string[];
  limit?: number;
  offset?: number;
  fields?: string[];
}) => {
  try {
    const query: any = {};

    if (params?.filters) {
      query.filter = params.filters;
    }

    if (params?.sort) {
      query.sort = params.sort;
    }

    if (params?.limit) {
      query.limit = params.limit;
    }

    if (params?.offset) {
      query.offset = params.offset;
    }

    if (params?.fields) {
      query.fields = params.fields;
    }

    const response = await directusClient.request(
      readItemsAny(CREATURES_COLLECTION as any, query) as any
    );

    return {
      data: response,
      meta: {
        pagination: {
          page: Math.floor((params?.offset || 0) / (params?.limit || 25)) + 1,
          pageSize: params?.limit || 25,
          pageCount: Math.ceil(response.length / (params?.limit || 25)),
          total: response.length,
        },
      },
    };
  } catch (error: any) {
    console.error('Error fetching creatures:', error);
    throw handleDirectusError(error, 'getCreatures');
  }
};

// 全恐竜データの取得（ページネーション対応）
export const getAllCreatures = async (params?: {
  filters?: Record<string, any>;
  sort?: string[];
  fields?: string[];
}) => {
  try {
    const allCreatures: any[] = [];
    let offset = 0;
    const limit = 100;
    let hasMore = true;

    while (hasMore) {
      const response = await directusClient.request(
        readItemsAny(CREATURES_COLLECTION as any, {
          ...params,
          limit,
          offset,
        })
      );

      allCreatures.push(...response);

      // 取得したデータがページサイズより少ない場合は最後のページ
      hasMore = response.length === limit;
      offset += limit;
    }

    return {
      data: allCreatures,
      meta: {
        pagination: {
          page: 1,
          pageSize: allCreatures.length,
          pageCount: 1,
          total: allCreatures.length,
        },
      },
    };
  } catch (error: any) {
    console.error('Error fetching all creatures:', error);
    throw handleDirectusError(error, 'getAllCreatures');
  }
};

export const getCreatureById = async (id: string, fields?: string[]) => {
  try {
    const query: any = {};
    if (fields) {
      query.fields = fields;
    }

    return await directusClient.request(readItemAny(CREATURES_COLLECTION as any, id, query) as any);
  } catch (error: any) {
    console.error('Error fetching creature by ID:', error);
    throw error;
  }
};

export const getCreaturesByCategory = async (category: string, fields?: string[]) => {
  try {
    const query: any = {
      filter: {
        category: {
          _eq: category,
        },
      },
    };

    if (fields) {
      query.fields = fields;
    }

    return await directusClient.request(readItemsAny(CREATURES_COLLECTION as any, query) as any);
  } catch (error: any) {
    console.error('Error fetching creatures by category:', error);
    throw error;
  }
};

// 地質時代データの取得
export const getAges = async (params?: {
  filters?: Record<string, any>;
  sort?: string[];
  limit?: number;
  offset?: number;
  fields?: string[];
}) => {
  try {
    const query: any = {};

    if (params?.filters) {
      query.filter = params.filters;
    }

    if (params?.sort) {
      query.sort = params.sort;
    }

    if (params?.limit) {
      query.limit = params.limit;
    }

    if (params?.offset) {
      query.offset = params.offset;
    }

    if (params?.fields) {
      query.fields = params.fields;
    }

    const response = await directusClient.request(
      readItemsAny(AGES_COLLECTION as any, query) as any
    );

    return {
      data: response,
      meta: {
        pagination: {
          page: Math.floor((params?.offset || 0) / (params?.limit || 25)) + 1,
          pageSize: params?.limit || 25,
          pageCount: Math.ceil(response.length / (params?.limit || 25)),
          total: response.length,
        },
      },
    };
  } catch (error: any) {
    console.error('Error fetching ages:', error);
    throw error;
  }
};

export const getAgeById = async (id: string, fields?: string[]) => {
  try {
    const query: any = {};
    if (fields) {
      query.fields = fields;
    }

    return await directusClient.request(readItemAny(AGES_COLLECTION as any, id, query) as any);
  } catch (error: any) {
    console.error('Error fetching age by ID:', error);
    throw error;
  }
};

// 関連データを含むagesの取得
export const getAgesWithRelations = async (params?: {
  filters?: Record<string, any>;
  sort?: string[];
  limit?: number;
  offset?: number;
}) => {
  try {
    const query: any = {
      fields: [
        'id',
        'name',
        'description',
        'start_year_in_million',
        'north_american_stage',
        'chinese_stage',
        'australian_stage',
        'date_created',
        'date_updated',
        'epoch.id',
        'epoch.name',
        'epoch.period.id',
        'epoch.period.name',
        'epoch.period.era.id',
        'epoch.period.era.name',
        'epoch.period.era.eon.id',
        'epoch.period.era.eon.name',
      ],
    };

    if (params?.filters) {
      query.filter = params.filters;
    }

    if (params?.sort) {
      // Directusのsortパラメータは文字列の配列として渡す
      query.sort = params.sort;
    }

    if (params?.limit) {
      query.limit = params.limit;
    }

    if (params?.offset) {
      query.offset = params.offset;
    }

    const response = await directusClient.request(
      readItemsAny(AGES_COLLECTION as any, query) as any
    );

    // responseが配列でない場合は空配列を返す
    const data = Array.isArray(response) ? response : [];

    return {
      data: data,
      meta: {
        pagination: {
          page: Math.floor((params?.offset || 0) / (params?.limit || 25)) + 1,
          pageSize: params?.limit || 25,
          pageCount: Math.ceil(data.length / (params?.limit || 25)),
          total: data.length,
        },
      },
    };
  } catch (error: any) {
    console.error('Error fetching ages with relations:', error);
    throw error;
  }
};

export const getEras = async (params?: {
  filters?: Record<string, any>;
  sort?: string[];
  limit?: number;
  offset?: number;
  fields?: string[];
}) => {
  try {
    const query: any = {};

    if (params?.filters) {
      query.filter = params.filters;
    }

    if (params?.sort) {
      query.sort = params.sort;
    }

    if (params?.limit) {
      query.limit = params.limit;
    }

    if (params?.offset) {
      query.offset = params.offset;
    }

    if (params?.fields) {
      query.fields = params.fields;
    }

    const response = await directusClient.request(
      readItemsAny(ERAS_COLLECTION as any, query) as any
    );

    return {
      data: response,
      meta: {
        pagination: {
          page: Math.floor((params?.offset || 0) / (params?.limit || 25)) + 1,
          pageSize: params?.limit || 25,
          pageCount: Math.ceil(response.length / (params?.limit || 25)),
          total: response.length,
        },
      },
    };
  } catch (error: any) {
    console.error('Error fetching eras:', error);
    throw error;
  }
};

export const getEraById = async (id: string, fields?: string[]) => {
  try {
    const query: any = {};
    if (fields) {
      query.fields = fields;
    }

    return await directusClient.request(readItemAny(ERAS_COLLECTION as any, id, query) as any);
  } catch (error: any) {
    console.error('Error fetching era by ID:', error);
    throw error;
  }
};

export const getPeriods = async (params?: {
  filters?: Record<string, any>;
  sort?: string[];
  limit?: number;
  offset?: number;
  fields?: string[];
}) => {
  try {
    const query: any = {};

    if (params?.filters) {
      query.filter = params.filters;
    }

    if (params?.sort) {
      query.sort = params.sort;
    }

    if (params?.limit) {
      query.limit = params.limit;
    }

    if (params?.offset) {
      query.offset = params.offset;
    }

    if (params?.fields) {
      query.fields = params.fields;
    }

    const response = await directusClient.request(
      readItemsAny(PERIODS_COLLECTION as any, query) as any
    );

    return {
      data: response,
      meta: {
        pagination: {
          page: Math.floor((params?.offset || 0) / (params?.limit || 25)) + 1,
          pageSize: params?.limit || 25,
          pageCount: Math.ceil(response.length / (params?.limit || 25)),
          total: response.length,
        },
      },
    };
  } catch (error: any) {
    console.error('Error fetching periods:', error);
    throw error;
  }
};

export const getPeriodById = async (id: string, fields?: string[]) => {
  try {
    const query: any = {};
    if (fields) {
      query.fields = fields;
    }

    return await directusClient.request(readItemAny(PERIODS_COLLECTION as any, id, query) as any);
  } catch (error: any) {
    console.error('Error fetching period by ID:', error);
    throw error;
  }
};

export const getEons = async (params?: {
  filters?: Record<string, any>;
  sort?: string[];
  limit?: number;
  offset?: number;
  fields?: string[];
}) => {
  try {
    const query: any = {};

    if (params?.filters) {
      query.filter = params.filters;
    }

    if (params?.sort) {
      query.sort = params.sort;
    }

    if (params?.limit) {
      query.limit = params.limit;
    }

    if (params?.offset) {
      query.offset = params.offset;
    }

    if (params?.fields) {
      query.fields = params.fields;
    }

    const response = await directusClient.request(
      readItemsAny(EONS_COLLECTION as any, query) as any
    );

    return {
      data: response,
      meta: {
        pagination: {
          page: Math.floor((params?.offset || 0) / (params?.limit || 25)) + 1,
          pageSize: params?.limit || 25,
          pageCount: Math.ceil(response.length / (params?.limit || 25)),
          total: response.length,
        },
      },
    };
  } catch (error: any) {
    console.error('Error fetching eons:', error);
    throw error;
  }
};

export const getEonById = async (id: string, fields?: string[]) => {
  try {
    const query: any = {};
    if (fields) {
      query.fields = fields;
    }

    return await directusClient.request(readItemAny(EONS_COLLECTION as any, id, query) as any);
  } catch (error: any) {
    console.error('Error fetching eon by ID:', error);
    throw error;
  }
};

export const getEpochs = async (params?: {
  filters?: Record<string, any>;
  sort?: string[];
  limit?: number;
  offset?: number;
  fields?: string[];
}) => {
  try {
    const query: any = {};

    if (params?.filters) {
      query.filter = params.filters;
    }

    if (params?.sort) {
      query.sort = params.sort;
    }

    if (params?.limit) {
      query.limit = params.limit;
    }

    if (params?.offset) {
      query.offset = params.offset;
    }

    if (params?.fields) {
      query.fields = params.fields;
    }

    const response = await directusClient.request(
      readItemsAny(EPOCHS_COLLECTION as any, query) as any
    );

    return {
      data: response,
      meta: {
        pagination: {
          page: Math.floor((params?.offset || 0) / (params?.limit || 25)) + 1,
          pageSize: params?.limit || 25,
          pageCount: Math.ceil(response.length / (params?.limit || 25)),
          total: response.length,
        },
      },
    };
  } catch (error: any) {
    console.error('Error fetching epochs:', error);
    throw error;
  }
};

export const getEpochById = async (id: string, fields?: string[]) => {
  try {
    const query: any = {};
    if (fields) {
      query.fields = fields;
    }

    return await directusClient.request(readItemAny(EPOCHS_COLLECTION as any, id, query) as any);
  } catch (error: any) {
    console.error('Error fetching epoch by ID:', error);
    throw error;
  }
};

// 検索機能
export const searchCreatures = async (searchTerm: string, fields?: string[]) => {
  try {
    const query: any = {
      filter: {
        _or: [
          {
            name: {
              _icontains: searchTerm,
            },
          },
          {
            description: {
              _icontains: searchTerm,
            },
          },
        ],
      },
    };

    if (fields) {
      query.fields = fields;
    }

    return await directusClient.request(readItemsAny(CREATURES_COLLECTION as any, query) as any);
  } catch (error: any) {
    console.error('Error searching creatures:', error);
    throw error;
  }
};

// 統計情報
export const getCreatureCount = async () => {
  try {
    const response = await directusClient.request(
      readItemsAny(CREATURES_COLLECTION as any, {
        fields: ['id'],
      })
    );
    return response.length;
  } catch (error: any) {
    console.error('Error getting creature count:', error);
    throw error;
  }
};

export const getAgeCount = async () => {
  try {
    const response = await directusClient.request(
      readItemsAny(AGES_COLLECTION as any, {
        fields: ['id'],
      })
    );
    return response.length;
  } catch (error: any) {
    console.error('Error getting age count:', error);
    throw error;
  }
};

// データ変換ヘルパー関数（現在未使用）
// const transformAiArticleData = (article: any): any => {
//   if (!article) {
//     throw new Error('Article data is undefined');
//   }

//   return {
//     ...article,
//     // JSON文字列を適切な型に変換
//     geographical_location: article.geographical_location
//       ? typeof article.geographical_location === 'string'
//         ? JSON.parse(article.geographical_location)
//         : article.geographical_location
//       : [],
//     discovery_type: article.discovery_type
//       ? typeof article.discovery_type === 'string'
//         ? JSON.parse(article.discovery_type)
//         : article.discovery_type
//       : [],
//     images: article.images
//       ? typeof article.images === 'string'
//         ? JSON.parse(article.images)
//         : article.images
//       : [],
//     keywords: article.keywords
//       ? typeof article.keywords === 'string'
//         ? JSON.parse(article.keywords)
//         : article.keywords
//       : [],
//   };
// };

// Duplication Articles データの取得
export const getDuplicationArticles = async (params?: {
  filters?: Record<string, any>;
  sort?: string[];
  limit?: number;
  offset?: number;
  fields?: string[];
}) => {
  try {
    const query: any = {};

    if (params?.filters) {
      query.filter = params.filters;
    }

    if (params?.sort) {
      query.sort = params.sort;
    }

    if (params?.limit) {
      query.limit = params.limit;
    }

    if (params?.offset) {
      query.offset = params.offset;
    }

    if (params?.fields) {
      query.fields = params.fields;
    }

    const response = await directusClient.request(
      readItemsAny(DUPLICATION_ARTICLES_COLLECTION as any, query) as any
    );

    return {
      data: response || [],
      meta: {
        pagination: {
          page: Math.floor((params?.offset || 0) / (params?.limit || 25)) + 1,
          pageSize: params?.limit || 25,
          pageCount: Math.ceil((response || []).length / (params?.limit || 25)),
          total: (response || []).length,
        },
      },
    };
  } catch (error: any) {
    console.error('Error fetching duplication articles:', error);
    throw error;
  }
};

export const getDuplicationArticleById = async (id: string, fields?: string[]) => {
  try {
    const query: any = {};
    if (fields) {
      query.fields = fields;
    }

    return await directusClient.request(
      readItemAny(DUPLICATION_ARTICLES_COLLECTION as any, id, query) as any
    );
  } catch (error: any) {
    console.error('Error fetching duplication article by ID:', error);
    throw error;
  }
};

// 重複グループを取得
export const getDuplicationGroups = async () => {
  try {
    const response = await directusClient.request(
      readItemsAny(DUPLICATION_ARTICLES_COLLECTION as any, {
        fields: ['*', 'article_id.title', 'article_id.id'],
        sort: ['group_id', 'is_parent', 'created_at'],
      })
    );

    // グループごとに整理
    const groups: Record<string, any[]> = {};
    (response || []).forEach((item: any) => {
      if (!groups[item.group_id]) {
        groups[item.group_id] = [];
      }
      groups[item.group_id].push(item);
    });

    return Object.values(groups).map(group => ({
      group_id: group[0].group_id,
      articles: group,
      parent_article: group.find(item => item.is_parent),
      child_articles: group.filter(item => !item.is_parent),
    }));
  } catch (error: any) {
    console.error('Error fetching duplication groups:', error);
    throw error;
  }
};

// 重複記事を作成
export const createDuplicationArticle = async (data: {
  group_id: string;
  article_id: number;
  is_parent: boolean;
  similarity_score?: number;
}) => {
  try {
    return await directusClient.request(
      createItemAny(DUPLICATION_ARTICLES_COLLECTION as any, {
        ...data,
        created_at: new Date().toISOString(),
      })
    );
  } catch (error: any) {
    console.error('Error creating duplication article:', error);
    throw error;
  }
};

// 重複記事を更新
export const updateDuplicationArticle = async (
  id: string,
  data: {
    is_parent?: boolean;
    similarity_score?: number;
  }
) => {
  try {
    return await directusClient.request(
      updateItemAny(DUPLICATION_ARTICLES_COLLECTION as any, id, data)
    );
  } catch (error: any) {
    console.error('Error updating duplication article:', error);
    throw error;
  }
};

// 重複記事を削除
export const deleteDuplicationArticle = async (id: string) => {
  try {
    return await directusClient.request(deleteItemAny(DUPLICATION_ARTICLES_COLLECTION as any, id));
  } catch (error: any) {
    console.error('Error deleting duplication article:', error);
    throw error;
  }
};

// 重複統計を取得
export const getDuplicationStats = async () => {
  try {
    const response = await directusClient.request(
      readItemsAny(DUPLICATION_ARTICLES_COLLECTION as any, {
        fields: ['group_id', 'similarity_score', 'is_parent'],
      })
    );

    const groups = new Set();
    const duplicates = (response || []).filter((item: any) => !item.is_parent);
    const similarityScores = duplicates
      .map((item: any) => item.similarity_score)
      .filter((score: any) => score !== null && typeof score === 'number');

    return {
      total_groups: groups.size,
      total_duplicates: duplicates.length,
      average_similarity:
        similarityScores.length > 0
          ? similarityScores.reduce((sum: number, score: number) => sum + score, 0) /
            similarityScores.length
          : 0,
      max_similarity: similarityScores.length > 0 ? Math.max(...similarityScores) : 0,
      min_similarity: similarityScores.length > 0 ? Math.min(...similarityScores) : 0,
    };
  } catch (error: any) {
    console.error('Error fetching duplication stats:', error);
    throw error;
  }
};

// AI記事の取得
export const getAiArticles = async (params?: {
  filters?: Record<string, any>;
  sort?: string[];
  limit?: number;
  offset?: number;
  fields?: string[];
}) => {
  try {
    // データ取得用のクエリ
    const dataQuery: any = {
      fields: params?.fields || ['*'],
    };

    if (params?.filters) {
      dataQuery.filter = params.filters;
    }

    if (params?.sort) {
      dataQuery.sort = params.sort;
    }

    if (params?.limit) {
      dataQuery.limit = params.limit;
    } else {
      // デフォルトのlimitを明示的に設定（Directusのデフォルトは25）
      dataQuery.limit = 1000; // 大きな値を設定して制限をテスト
    }

    if (params?.offset) {
      dataQuery.offset = params.offset;
    }

    // デバッグ: クエリパラメータを確認

    // 総数取得用のクエリ（limitとoffsetは除外）
    const countQuery: any = {
      fields: ['id'],
      limit: 1000, // 大きな値を設定して制限をテスト
    };

    if (params?.filters) {
      countQuery.filter = params.filters;
    }

    // 並行してデータと総数を取得
    const [dataResponse, countResponse] = await Promise.all([
      directusClient.request(readItemsAny(AI_ARTICLES_COLLECTION as any, dataQuery)),
      directusClient.request(readItemsAny(AI_ARTICLES_COLLECTION as any, countQuery)),
    ]);

    // 総数を取得（countResponseはidの配列なので、その長さを使用）
    const total = Array.isArray(countResponse) ? countResponse.length : dataResponse.length;

    const pageSize = params?.limit || 25;
    const currentPage = Math.floor((params?.offset || 0) / pageSize) + 1;
    const pageCount = Math.ceil(total / pageSize);

    return {
      data: dataResponse,
      meta: {
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          pageCount: pageCount,
          total: total,
        },
      },
    };
  } catch (error: any) {
    console.error('Error fetching AI articles:', error);

    // 認証エラーの場合はモックデータを返す
    if (
      process.env.NODE_ENV === 'development' &&
      (error?.status === 401 || error?.message?.includes('Invalid user credentials'))
    ) {
      console.warn('🔐 Directus認証エラーのため、モックデータを表示します');
      console.warn('📝 管理者に新しいトークンの取得を依頼してください');

      return {
        data: [
          {
            id: 'mock-1',
            title: '恐竜の進化について',
            content:
              'これはモックデータです。Directusの認証が解決されると実際のデータが表示されます。',
            date_created: new Date().toISOString(),
            is_important: true,
            excerpt: '恐竜の進化について詳しく解説します。',
            featured_image: null,
            author: 'システム管理者',
          },
          {
            id: 'mock-2',
            title: '地質時代の恐竜たち',
            content: '地質時代ごとの恐竜の特徴と生息環境について説明します。',
            date_created: new Date(Date.now() - 86400000).toISOString(),
            is_important: true,
            excerpt: '地質時代の恐竜について学びましょう。',
            featured_image: null,
            author: 'システム管理者',
          },
        ],
        meta: {
          pagination: {
            page: 1,
            pageSize: 12,
            pageCount: 1,
            total: 2,
          },
        },
      };
    }

    throw handleDirectusError(error, 'getAiArticles');
  }
};

// AI記事のIDによる取得
export const getAiArticleById = async (id: string | number) => {
  try {
    // デバッグ: レスポンスの構造を確認
    return await directusClient.request(readItemAny(AI_ARTICLES_COLLECTION as any, id) as any);
  } catch (error: any) {
    console.error('Error fetching AI article by ID:', error);
    throw error;
  }
};

// Directusのヘルスチェック
export const checkDirectusHealth = async (): Promise<{
  success: boolean;
  message: string;
  data?: any;
  error?: string;
  status: 'healthy' | 'unhealthy';
}> => {
  try {
    const response = await directusClient.request(
      readItemsAny(CREATURES_COLLECTION as any, {
        limit: 1,
      })
    );
    return {
      success: true,
      message: 'Directus is healthy',
      data: response,
      status: 'healthy',
    };
  } catch (error: any) {
    return {
      success: false,
      message: 'Directus health check failed',
      error: error instanceof Error ? error.message : 'Unknown error',
      status: 'unhealthy',
    };
  }
};

// コメント関連の関数
export const getComments = async (params?: {
  filters?: Record<string, any>;
  sort?: string[];
  limit?: number;
  offset?: number;
  fields?: string[];
}) => {
  try {
    const query: any = {};

    if (params?.filters) {
      query.filter = params.filters;
    }

    if (params?.sort) {
      query.sort = params.sort;
    }

    if (params?.limit) {
      query.limit = params.limit;
    }

    if (params?.offset) {
      query.offset = params.offset;
    }

    if (params?.fields) {
      query.fields = params.fields;
    }

    const response = await directusClient.request(
      readItemsAny(COMMENTS_COLLECTION as any, query) as any
    );

    return {
      data: response,
      meta: {
        pagination: {
          page: Math.floor((params?.offset || 0) / (params?.limit || 25)) + 1,
          pageSize: params?.limit || 25,
          pageCount: Math.ceil(response.length / (params?.limit || 25)),
          total: response.length,
        },
      },
    };
  } catch (error: any) {
    const errorInfo = handleDirectusError(error, 'getComments');
    console.error('Error fetching comments:', errorInfo);
    throw new Error(errorInfo.message);
  }
};

export const getCommentById = async (id: string, fields?: string[]) => {
  try {
    const query: any = {};
    if (fields) {
      query.fields = fields;
    }

    return await directusClient.request(readItemAny(COMMENTS_COLLECTION as any, id, query) as any);
  } catch (error: any) {
    console.error('Error fetching comment by ID:', error);
    throw error;
  }
};

export const getCommentsByArticle = async (articleId: string) => {
  try {
    return await directusClient.request(
      readItemsAny(COMMENTS_COLLECTION as any, {
        filter: {
          article: {
            _eq: articleId,
          },
        },
        sort: ['date_created'],
      })
    );
  } catch (error: any) {
    console.error('Error fetching comments by article:', error);
    throw error;
  }
};

export const getCommentsByCreature = async (creatureId: string) => {
  try {
    return await directusClient.request(
      readItemsAny(COMMENTS_COLLECTION as any, {
        filter: {
          creature: {
            _eq: creatureId,
          },
        },
        sort: ['date_created'],
      })
    );
  } catch (error: any) {
    console.error('Error fetching comments by creature:', error);
    throw error;
  }
};

export const getCommentsByUser = async (userId: string) => {
  try {
    return await directusClient.request(
      readItemsAny(COMMENTS_COLLECTION as any, {
        filter: {
          author: {
            _eq: userId,
          },
        },
        sort: ['-date_created'],
      })
    );
  } catch (error: any) {
    console.error('Error fetching comments by user:', error);
    throw error;
  }
};

export const createComment = async (commentData: any) => {
  try {
    return await directusClient.request(
      createItemAny(COMMENTS_COLLECTION as any, {
        ...commentData,
        is_approved: false,
        is_spam: false,
      })
    );
  } catch (error: any) {
    console.error('Error creating comment:', error);
    throw error;
  }
};

export const updateComment = async (id: string, commentData: any) => {
  try {
    return await directusClient.request(updateItemAny(COMMENTS_COLLECTION as any, id, commentData));
  } catch (error: any) {
    console.error('Error updating comment:', error);
    throw error;
  }
};

export const deleteComment = async (id: string) => {
  try {
    await directusClient.request(deleteItemAny(COMMENTS_COLLECTION as any, id));
    return true;
  } catch (error: any) {
    console.error('Error deleting comment:', error);
    throw error;
  }
};

export const getCommentCount = async () => {
  try {
    const response = await directusClient.request(
      readItemsAny(COMMENTS_COLLECTION as any, {
        fields: ['id'],
      })
    );
    return response.length;
  } catch (error: any) {
    console.error('Error getting comment count:', error);
    throw error;
  }
};

export const getCommentCountByArticle = async (articleId: string) => {
  try {
    const response = await directusClient.request(
      readItemsAny(COMMENTS_COLLECTION as any, {
        filter: {
          article: {
            _eq: articleId,
          },
        },
        fields: ['id'],
      })
    );
    return response.length;
  } catch (error: any) {
    console.error('Error getting comment count by article:', error);
    throw error;
  }
};

export const getCommentCountByCreature = async (creatureId: string) => {
  try {
    const response = await directusClient.request(
      readItemsAny(COMMENTS_COLLECTION as any, {
        filter: {
          creature: {
            _eq: creatureId,
          },
        },
        fields: ['id'],
      })
    );
    return response.length;
  } catch (error: any) {
    console.error('Error getting comment count by creature:', error);
    throw error;
  }
};

export const searchComments = async (searchTerm: string, limit?: number) => {
  try {
    const query: any = {
      filter: {
        _or: [
          {
            content: {
              _icontains: searchTerm,
            },
          },
        ],
      },
    };

    if (limit) {
      query.limit = limit;
    }

    return await directusClient.request(readItemsAny(COMMENTS_COLLECTION as any, query) as any);
  } catch (error: any) {
    console.error('Error searching comments:', error);
    throw error;
  }
};
